// src/components/HeroVideoSection.tsx

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// IMPORTANT: Import your actual video file here
import myHeroVideo from '../assets/demo.mp4';
import bgTransparent from '../assets/bg.jpg';

// Define the type for a single stat item
interface StatItem {
  id: number;
  labelKey: string;
  value: number;
  unit?: string;
  prefix?: string;
  colorClass: string;
}

// Custom hook for counting animation
const useCountUp = (end: number, duration: number = 2000) => {
  const [count, setCount] = useState(0);
  const startTime = React.useRef(0);
  const animationFrameId = React.useRef<number | null>(null);

  useEffect(() => {
    startTime.current = performance.now();

    const animate = (currentTime: DOMHighResTimeStamp) => {
      const elapsed = currentTime - startTime.current;
      const progress = Math.min(elapsed / duration, 1);
      setCount(Math.floor(progress * end));

      if (progress < 1) {
        animationFrameId.current = requestAnimationFrame(animate);
      }
    };

    animationFrameId.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [end, duration]);

  return count;
};

// Component for a single stat card with animation
const StatCard: React.FC<{ stat: StatItem }> = ({ stat }) => {
  const count = useCountUp(stat.value, 2000);
  const { t } = useTranslation();

  const displayValue = stat.value === 1.5 && count === 1 ? '1.5' : count.toLocaleString();

  return (
    <div className="text-center text-white p-4">
      <p className={`text-5xl md:text-6xl font-extrabold mb-2 leading-none ${stat.colorClass}`}>
        {stat.prefix}{displayValue}{stat.unit}
      </p>
      <p className="text-gray-300 text-lg">{t(stat.labelKey)}</p>
    </div>
  );
};


const HeroVideoSection: React.FC = () => {
  const { t } = useTranslation();

  const stats: StatItem[] = [
    { id: 1, labelKey: 'stats_spam_calls_blocked', value: 600, unit: 'm', colorClass: 'text-orange-400' },
    { id: 2, labelKey: 'stats_installs_nationwide', value: 12, unit: 'm', colorClass: 'text-purple-400' },
    // { id: 3, labelKey: 'stats_prevented_losses', value: 740, unit: 'm', prefix: '$', colorClass: 'text-green-400' },
    { id: 4, labelKey: 'stats_scammers_time_wasted', value: 500, unit: 'm', colorClass: 'text-blue-400' },
    { id: 5, labelKey: 'stats_phone_numbers_database', value: 1.5, unit: 'b', colorClass: 'text-red-400' },
  ];

  return (
    // Adjust vertical padding for responsiveness: py-8 on mobile, py-16 on larger screens
    <section className="relative bg-black py-8 md:py-16 flex flex-col items-center justify-center ">

      {/* Remove fixed height, use min-h if necessary, or let content dictate height */}
      {/* max-w-4xl already helps in making it smaller */}
      <div className="w-full max-w-4xl px-4 mx-auto "> {/* Removed h-[800px] */}
         {/* Transparent Background Image */}
      <img
        src={bgTransparent}
        alt={t('decorative_background_alt')}
        className="absolute top-0 left-1/2 transform -translate-x-1/2 w-full opacity-10 pointer-events-none select-none"
      />

        {/* Heading for the section - "We're winning the fight against spam" */}
        <h2 className="text-white text-4xl md:text-5xl font-bold text-center mb-12">
          {t('winning_spam_fight_heading')}
        </h2>

        {/* Statistics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {stats.map((stat) => (
            <StatCard key={stat.id} stat={stat} />
          ))}
        </div>

        {/* Video Heading */}
        <h3 className="text-white text-3xl md:text-4xl font-bold text-center mb-10">
          {t('video_reintroducing_coc_heading')}
        </h3>

        {/* Video container for responsive 16:9 aspect ratio */}
        <div className="relative w-full pb-[56.25%] h-0 rounded-lg overflow-hidden shadow-2xl">
          <video
            className="absolute top-0 left-0 w-full h-full object-cover"
            controls
            autoPlay
            loop
            muted
            playsInline
          >
            <source src={myHeroVideo} type="video/mp4" />
            {t('video_tag_unsupported')}
          </video>
        </div>
      </div>
    </section>
  );
};

export default HeroVideoSection;